import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  api,
  API,
  absoluteImageUrl,
  authHeaders,
  clearAdminToken,
  formatApiErrorDetail,
  getAdminToken,
  setAdminToken,
} from "@/lib/api";
import { useAdminJobs } from "@/hooks/useAdminJobs";
import { Phone, LogOut, Upload, Trash2, Loader2, ImagePlus, CheckCircle2, AlertCircle, Lock } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/config/site";

const TID = {
  root: "admin-root",
  loginForm: "admin-login-form",
  loginEmail: "admin-login-email",
  // NOTE: value is a data-testid selector string used by E2E tests, NOT a
  // credential. Static scanners flag the word "password" here — safe to ignore.
  loginPassword: "admin-login-password",
  loginSubmit: "admin-login-submit",
  loginError: "admin-login-error",
  logout: "admin-logout-btn",
  uploadForm: "admin-upload-form",
  uploadTitle: "admin-upload-title",
  uploadDescription: "admin-upload-description",
  uploadTags: "admin-upload-tags",
  uploadImage: "admin-upload-image",
  uploadSubmit: "admin-upload-submit",
  uploadSuccess: "admin-upload-success",
  uploadError: "admin-upload-error",
  jobItem: (id) => `admin-job-${id}`,
  jobDelete: (id) => `admin-job-delete-${id}`,
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [checking, setChecking] = useState(!!getAdminToken());

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!getAdminToken()) return setChecking(false);
      try {
        await api.get("/admin/me", { headers: authHeaders() });
        if (!cancelled) setAuthed(true);
      } catch {
        clearAdminToken();
        if (!cancelled) setAuthed(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }
    check();
    return () => {
      cancelled = true;
    };
    // Mount-only check. All referenced identifiers (`api`, `authHeaders`,
    // `getAdminToken`, `clearAdminToken`) are module-level stable imports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-yellow-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid={TID.root}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 bg-white border-2 border-yellow-400 p-1 flex items-center justify-center">
              <img src="/images/logo.png" alt="BM Towing logo" className="max-h-full max-w-full object-contain" />
            </div>
            <div>
              <div className="font-display text-2xl font-black uppercase text-white leading-none">Admin Console</div>
              <div className="font-display text-[11px] tracking-[0.25em] text-yellow-400 uppercase mt-1">BM Towing &amp; Recovery</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="nav-call-btn"
              className="hidden sm:inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-display font-black uppercase tracking-wider text-sm px-4 py-2 border-2 border-black"
            >
              <Phone className="h-4 w-4" strokeWidth={3} /> Call {PHONE_DISPLAY}
            </a>
            <a
              href="/"
              className="font-display uppercase tracking-widest text-xs text-slate-400 hover:text-yellow-400"
            >
              ← Back to site
            </a>
          </div>
        </div>

        {!authed ? (
          <Login onSuccess={() => setAuthed(true)} />
        ) : (
          <Dashboard onLogout={() => { clearAdminToken(); setAuthed(false); }} />
        )}
      </div>
    </div>
  );
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState({ loading: false, error: "" });
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "" });
    try {
      const { data } = await api.post("/admin/login", { email: email.trim(), password });
      setAdminToken(data.access_token);
      setState({ loading: false, error: "" });
      onSuccess();
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Login failed.";
      setState({ loading: false, error: msg });
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={TID.loginForm}
      className="max-w-md mx-auto bg-slate-900 border-2 border-slate-700 p-6 sm:p-8 bm-hard-shadow"
      noValidate
    >
      <div className="flex items-center gap-2 font-display uppercase tracking-widest text-yellow-400 text-xs mb-4">
        <Lock className="h-4 w-4" strokeWidth={3} /> Admin Sign-In
      </div>
      <h1 className="font-display font-black uppercase text-white text-3xl mb-6">Sign in to post a job</h1>

      <label className="block mb-4">
        <span className="font-display uppercase tracking-widest text-[10px] text-slate-300">Email</span>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid={TID.loginEmail}
          className="mt-1 w-full bg-slate-950 border-2 border-slate-700 focus:border-yellow-400 text-slate-100 px-3 py-3 outline-none"
          placeholder="admin@bmtowingrecovery.com"
          autoComplete="email"
        />
      </label>

      <label className="block mb-6">
        <span className="font-display uppercase tracking-widest text-[10px] text-slate-300">Password</span>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid={TID.loginPassword}
          className="mt-1 w-full bg-slate-950 border-2 border-slate-700 focus:border-yellow-400 text-slate-100 px-3 py-3 outline-none"
          autoComplete="current-password"
        />
      </label>

      <button
        type="submit"
        disabled={state.loading}
        data-testid={TID.loginSubmit}
        className="w-full inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-70 text-slate-900 font-display font-black uppercase tracking-wider px-5 py-3 border-2 border-black min-h-[52px]"
      >
        {state.loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Signing in…</> : <>Sign In</>}
      </button>

      {state.error && (
        <div data-testid={TID.loginError} className="mt-4 flex items-start gap-2 border-2 border-red-500 bg-red-500/10 text-red-200 p-3 text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5" /> {state.error}
        </div>
      )}
    </form>
  );
}

function Dashboard({ onLogout }) {
  const { jobs, loading, error: listError, reload, remove } = useAdminJobs();

  const deleteJob = async (id) => {
    if (!window.confirm("Delete this job photo?")) return;
    try {
      await remove(id);
    } catch (e) {
      alert(formatApiErrorDetail(e?.response?.data?.detail) || "Could not delete.");
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="font-display uppercase tracking-widest text-yellow-400 text-sm">Signed in as admin</div>
        <button
          type="button"
          onClick={onLogout}
          data-testid={TID.logout}
          className="inline-flex items-center gap-2 font-display uppercase tracking-widest text-xs text-slate-300 hover:text-yellow-400 border-2 border-slate-700 hover:border-yellow-400 px-3 py-2"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      <UploadForm onUploaded={reload} />

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-black uppercase text-white text-2xl">Recent Uploads</h2>
          <button
            type="button"
            onClick={reload}
            className="font-display uppercase tracking-widest text-[10px] text-slate-400 hover:text-yellow-400"
          >
            Refresh
          </button>
        </div>
        {loading && <div className="text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {listError && <div className="text-red-300 text-sm">{listError}</div>}
        {!loading && jobs.length === 0 && (
          <div className="text-slate-400 text-sm bg-slate-900 border-2 border-slate-700 p-5">
            No uploads yet. Post your first job photo above — it will instantly appear on the home page.
          </div>
        )}
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map((j) => (
            <li
              key={j.id}
              data-testid={TID.jobItem(j.id)}
              className="relative bg-slate-900 border-2 border-slate-700 overflow-hidden"
            >
              <div className="aspect-[4/3] w-full bg-slate-950">
                <img src={absoluteImageUrl(j.image_url)} alt={j.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <div className="font-display font-black uppercase text-white text-sm truncate">{j.title}</div>
                {j.description && <div className="text-slate-400 text-xs mt-1 line-clamp-2">{j.description}</div>}
                {j.tags?.length ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {j.tags.map((t) => (
                      <span key={t} className="text-[9px] font-display uppercase tracking-widest bg-yellow-400 text-slate-900 px-1.5 py-0.5">{t}</span>
                    ))}
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={() => deleteJob(j.id)}
                  data-testid={TID.jobDelete(j.id)}
                  className="mt-3 inline-flex items-center gap-2 text-xs text-red-300 hover:text-red-200"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <div className="border-t border-slate-800 pt-6 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <Phone className="h-3 w-3" /> 24/7 Line: <span className="text-yellow-400">(425) 647-9585</span>
        </div>
      </div>
    </div>
  );
}

function UploadForm({ onUploaded }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      setState({ loading: false, ok: false, error: "Please choose a photo to upload." });
      return;
    }
    setState({ loading: true, ok: false, error: "" });
    try {
      const fd = new FormData();
      fd.append("title", title.trim() || "New Recovery Job");
      fd.append("description", description.trim());
      fd.append("tags", tags.trim());
      fd.append("image", file);
      await axios.post(`${API}/admin/jobs`, fd, {
        headers: { ...authHeaders() },
      });
      setState({ loading: false, ok: true, error: "" });
      setTitle(""); setDescription(""); setTags(""); setFile(null); setPreview("");
      onUploaded?.();
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setState({ loading: false, ok: false, error: msg });
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={TID.uploadForm}
      className="bg-slate-900 border-2 border-yellow-400 bm-hard-shadow-yellow p-5 sm:p-6"
      noValidate
    >
      <div className="flex items-center gap-2 font-display uppercase tracking-widest text-yellow-400 text-xs">
        <ImagePlus className="h-4 w-4" strokeWidth={3} /> Post a New Job Photo
      </div>
      <h2 className="mt-1 font-display font-black uppercase text-white text-2xl sm:text-3xl">
        Add today's job to the site
      </h2>
      <p className="mt-2 text-slate-400 text-sm">
        Snap a photo on your phone, drop it here, and it goes live on the home
        page instantly.
      </p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block sm:col-span-2">
          <span className="font-display uppercase tracking-widest text-[10px] text-slate-300">Photo *</span>
          <div className="mt-1 flex items-center gap-3">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onFile}
              data-testid={TID.uploadImage}
              required
              className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:border-2 file:border-black file:bg-yellow-400 file:text-slate-900 file:font-display file:font-black file:uppercase file:tracking-widest text-slate-200"
            />
          </div>
          {preview && (
            <div className="mt-3 border-2 border-slate-700">
              <img src={preview} alt="Preview" className="w-full max-h-64 object-contain bg-slate-950" />
            </div>
          )}
        </label>

        <label className="block sm:col-span-2">
          <span className="font-display uppercase tracking-widest text-[10px] text-slate-300">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid={TID.uploadTitle}
            placeholder="e.g. Flatbed tow — Bellevue"
            className="mt-1 w-full bg-slate-950 border-2 border-slate-700 focus:border-yellow-400 text-slate-100 px-3 py-3 outline-none"
          />
        </label>

        <label className="block sm:col-span-2">
          <span className="font-display uppercase tracking-widest text-[10px] text-slate-300">Description</span>
          <textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid={TID.uploadDescription}
            placeholder="Short caption — what happened, where, how it went."
            className="mt-1 w-full bg-slate-950 border-2 border-slate-700 focus:border-yellow-400 text-slate-100 px-3 py-3 outline-none resize-y"
          />
        </label>

        <label className="block sm:col-span-2">
          <span className="font-display uppercase tracking-widest text-[10px] text-slate-300">Tags (comma-separated)</span>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            data-testid={TID.uploadTags}
            placeholder="Flatbed, Bellevue, Luxury"
            className="mt-1 w-full bg-slate-950 border-2 border-slate-700 focus:border-yellow-400 text-slate-100 px-3 py-3 outline-none"
          />
        </label>
      </div>

      <button
        type="submit"
        disabled={state.loading}
        data-testid={TID.uploadSubmit}
        className="mt-6 inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-70 text-slate-900 font-display font-black uppercase tracking-wider px-6 py-3 border-2 border-black min-h-[52px]"
      >
        {state.loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Uploading…</> : <><Upload className="h-5 w-5" strokeWidth={3} /> Post to Site</>}
      </button>

      {state.ok && (
        <div data-testid={TID.uploadSuccess} className="mt-4 flex items-start gap-2 border-2 border-green-500 bg-green-500/10 text-green-200 p-3 text-sm">
          <CheckCircle2 className="h-4 w-4 mt-0.5" /> Posted! It's now on the home page.
        </div>
      )}
      {state.error && (
        <div data-testid={TID.uploadError} className="mt-4 flex items-start gap-2 border-2 border-red-500 bg-red-500/10 text-red-200 p-3 text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5" /> {state.error}
        </div>
      )}
    </form>
  );
}
